import classNames from 'classnames';
import { format, isSameMonth, isToday } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { JobDropZone } from '~/components/PlannerView/JobDropZone';
import type { CalendarEvent } from '~/components/PlannerView/PlannerView';
import type { RescheduleDetails } from '~/components/PlannerView/types';
import { getScheduleEventCompositeKey } from '~/helpers/scheduleEventKey';
import type { useLocalStorage } from '~/hooks';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import {
  DATE_NUMBER_AREA_COMPACT_PX,
  DATE_NUMBER_AREA_PX,
  JOB_PILL_HEIGHT_PX,
  JOB_PILL_MARGIN_PX,
  RESERVED_BOTTOM_SPACE_PX,
} from './constants';
import type { Job, PlaceData } from './types';
import { createCellKey } from './utils';

type CalendarDateCellProps = {
  date: Date;
  jobs: Job[];
  placeData: PlaceData;
  onJobClick: (jobId: string, status?: string) => void;
  onReschedule?: (details: RescheduleDetails) => void;
  navigate: (path: string) => void;
  storage: ReturnType<typeof useLocalStorage>;
  opacity?: number;
  currentMonth: Date;
  cellHeight: number;
  overlayTopOffsetPx?: number;
  bookingsHidden: boolean;
  draftCellId: string | null;
  highlightedCells: Set<string>;
  setDraftCellId: React.Dispatch<React.SetStateAction<string | null>>;
  setHighlightedCells: React.Dispatch<React.SetStateAction<Set<string>>>;
  hiddenButtonCell: { siteId: string; date: string } | null;
  setHiddenButtonCell: React.Dispatch<
    React.SetStateAction<{ siteId: string; date: string } | null>
  >;
  setIsInJobCreationFlow: React.Dispatch<React.SetStateAction<boolean>>;
  selectedDayViewDate: string | null;
};

/**
 * Calendar date cell with JobDropZone functionality
 * Renders a single day cell in the monthly calendar grid
 */
const CalendarDateCell = React.memo<CalendarDateCellProps>(
  ({
    date,
    jobs,
    placeData,
    onJobClick,
    onReschedule,
    navigate,
    storage,
    currentMonth,
    cellHeight,
    overlayTopOffsetPx = 0,
    bookingsHidden,
    draftCellId,
    highlightedCells,
    setDraftCellId,
    setHighlightedCells,
    hiddenButtonCell,
    setHiddenButtonCell,
    setIsInJobCreationFlow,
    selectedDayViewDate,
  }) => {
    const { isTablet, isMobile } = useBreakpoint();
    const cellRef = useRef<HTMLDivElement>(null);
    const secondCellClickTimeoutRef = useRef<ReturnType<
      typeof setTimeout
    > | null>(null);

    useEffect(() => {
      // Clear any pending double-click timeout so it cannot fire after unmount
      return () => {
        if (secondCellClickTimeoutRef.current) {
          clearTimeout(secondCellClickTimeoutRef.current);
          secondCellClickTimeoutRef.current = null;
        }
      };
    }, []);

    // Memoize the custom navigation function to prevent JobDropZone re-renders
    const customNavigate = useCallback(
      (url: string) => {
        if (
          url.includes('/jobs/planner/new') ||
          url.includes('/jobs/calendar/new')
        ) {
          let siteId: string | null = null;
          let dateParam: string | null = null;
          if (typeof window !== 'undefined') {
            const urlObj = new URL(url, window.location.origin);
            siteId = urlObj.searchParams.get('siteId');
            dateParam = urlObj.searchParams.get('date');
          }
          const monthlyJobUrl = `new?siteId=${siteId}&date=${dateParam}`;
          navigate(monthlyJobUrl);
        } else {
          navigate(url);
        }
      },
      [navigate]
    );

    // Memoize the setJobNameWithEvent function to prevent JobDropZone re-renders
    const setJobNameWithEvent = useCallback(
      (key: string, value: string) => {
        try {
          storage.set(key, value);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('jobNameStorageChanged', {
                detail: { key, value },
              })
            );
          }
        } catch (error) {
          console.error('Failed to update job name storage:', error);
        }
      },
      [storage]
    );

    // Exclude bookings from overflow calculation
    const nonBookingJobs = useMemo(
      () => jobs.filter((j) => j.status !== 'Booking'),
      [jobs]
    );

    // Job visibility calculations
    const { visibleJobsCount, hiddenCount, hasOverflow, isSingleRow } =
      useMemo(() => {
        const pillHeight = isMobile ? 20 : JOB_PILL_HEIGHT_PX;
        const availableHeightForJobs =
          cellHeight - RESERVED_BOTTOM_SPACE_PX - DATE_NUMBER_AREA_PX;
        const jobsPerPill = pillHeight + JOB_PILL_MARGIN_PX;

        let maxVisibleJobs = Math.max(
          0,
          Math.floor(availableHeightForJobs / jobsPerPill)
        );
        if (isTablet || isMobile) maxVisibleJobs = Math.min(maxVisibleJobs, 1);
        if (nonBookingJobs.length > 0) {
          maxVisibleJobs = Math.max(1, maxVisibleJobs);
        }

        const hidden = Math.max(0, nonBookingJobs.length - maxVisibleJobs);
        return {
          visibleJobsCount: Math.min(nonBookingJobs.length, maxVisibleJobs),
          hiddenCount: hidden,
          hasOverflow: hidden > 0,
          isSingleRow: maxVisibleJobs === 1,
        } as const;
      }, [nonBookingJobs.length, cellHeight, isTablet, isMobile]);

    // Position overflow indicator
    const overflowTopPx = useMemo(() => {
      const pillHeight = isMobile ? 20 : JOB_PILL_HEIGHT_PX;
      return (
        DATE_NUMBER_AREA_COMPACT_PX -
        2 +
        visibleJobsCount * (pillHeight + JOB_PILL_MARGIN_PX)
      );
    }, [visibleJobsCount, isMobile]);

    // Memoize basic calculations
    const cellData = useMemo(
      () => ({
        isToday: isToday(date),
        dayNumber: format(date, 'd'),
        dateKey: format(date, 'yyyy-MM-dd'),
        siteId: placeData?.id || '',
      }),
      [date, placeData?.id]
    );

    // Convert jobs to CalendarEvent format for JobDropZone
    const cellEvents: CalendarEvent[] = useMemo(() => {
      if (jobs.length === 0) return [];

      return jobs.map((job, index) => ({
        y: 0,
        x: 0,
        event: {
          id: job.id,
          status: job.status,
          assignee: job.assignee,
          included: null,
          attributes:
            job.attributes?.map((attr) => ({
              id: attr.id,
              type: attr.type,
              category: attr.category,
              name: attr.name,
              value: attr.value,
            })) || null,
          tags: null,
          summary: job.summary,
          start: job.start,
          end: job.end,
          scheduledStartTime: job.scheduledStartTime,
          scheduledEndTime: job.scheduledEndTime,
          timeZone: job.timeZone,
        },
        eventIndex: index,
        siteId: cellData.siteId,
        compositeKey: getScheduleEventCompositeKey(job, cellData.siteId),
      }));
    }, [jobs, cellData.siteId]);

    // JobDropZone callbacks
    const jobDropZoneCallbacks = useMemo(
      () => ({
        onCellClick: (siteId: string, cellDate: Date) => {
          const dateString = format(cellDate, 'yyyy-MM-dd');
          navigate(`/jobs/calendar/${siteId}/day?date=${dateString}`);
        },
        onLongPress: (siteId: string, cellDate: Date) => {
          const cellId = createCellKey(siteId, cellDate);
          setDraftCellId(cellId);
          setHighlightedCells(new Set([cellId]));
          navigate(
            `new?siteId=${siteId}&date=${format(cellDate, 'yyyy-MM-dd')}`
          );
        },
        setDesktopDraftJobPill: () => {},
        onScrollToReveal: () => {},
        expandSite: () => {},
        onDragHover: () => {},
      }),
      [navigate, setDraftCellId, setHighlightedCells]
    );

    // Memoize static props for JobDropZone
    const dropZoneProps = useMemo(
      () => ({
        siteId: cellData.siteId,
        siteName: placeData?.name || '',
        cellEvents,
        isRowExpanded: true,
        bookingsHidden,
        jobDetail: false,
        start: date,
        data: [],
        location: {
          pathname:
            typeof window !== 'undefined' ? window.location.pathname : '',
        },
      }),
      [cellData.siteId, placeData?.name, cellEvents, date, bookingsHidden]
    );

    const isInSelectedMonth = useMemo(
      () => isSameMonth(date, currentMonth),
      [date, currentMonth]
    );

    const isDayViewOpen = useMemo(
      () => selectedDayViewDate === cellData.dateKey,
      [selectedDayViewDate, cellData.dateKey]
    );

    return (
      <div>
        <JobDropZone
          {...dropZoneProps}
          date={date}
          onReschedule={onReschedule}
          navigate={customNavigate}
          onCellClick={jobDropZoneCallbacks.onCellClick}
          onLongPress={jobDropZoneCallbacks.onLongPress}
          setDraftCellId={setDraftCellId}
          setHighlightedCells={setHighlightedCells}
          highlightedCells={highlightedCells}
          draftCellId={draftCellId}
          setJobNameWithEvent={setJobNameWithEvent}
          setDesktopDraftJobPill={jobDropZoneCallbacks.setDesktopDraftJobPill}
          storage={storage}
          hiddenButtonCell={hiddenButtonCell}
          setHiddenButtonCell={setHiddenButtonCell}
          setIsInJobCreationFlow={setIsInJobCreationFlow}
          onScrollToReveal={jobDropZoneCallbacks.onScrollToReveal}
          secondCellClickTimeoutRef={secondCellClickTimeoutRef}
          expandSite={jobDropZoneCallbacks.expandSite}
          onDragHover={jobDropZoneCallbacks.onDragHover}
          previewTop={() =>
            DATE_NUMBER_AREA_COMPACT_PX -
            4 +
            visibleJobsCount * (JOB_PILL_HEIGHT_PX + JOB_PILL_MARGIN_PX)
          }
        >
          <div
            ref={cellRef}
            className={classNames(
              'relative h-full w-full border-b border-r border-gray-200 bg-white p-1 pl-0 pt-0 transition-opacity duration-100',
              {
                'hover:bg-gray-50': isInSelectedMonth && !isDayViewOpen,
                '!bg-[#EB00FF1A]': cellData.isToday,
                '!bg-[#ea00ff32]': isDayViewOpen && cellData.isToday,
                '!bg-[#0070701A]': isDayViewOpen && !cellData.isToday,
              }
            )}
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              opacity: isInSelectedMonth ? 1 : 0.45,
            }}
          >
            {/* Booking placeholder area background */}
            {!bookingsHidden && (
              <div className='absolute left-0 right-0 top-0 h-7 bg-teal-300/10' />
            )}

            {/* Overflow indicator area */}
            <div
              className='flex flex-col pl-1 pt-1'
              style={{ marginTop: overlayTopOffsetPx }}
            >
              {!isMobile && hasOverflow && (
                <div
                  className={classNames(
                    'absolute z-[12] flex items-center gap-1 text-xs font-medium text-primary',
                    {
                      'right-2': !isMobile && isSingleRow,
                      'left-2': isMobile || !isSingleRow,
                    }
                  )}
                  style={{
                    top: Math.max(
                      isSingleRow
                        ? DATE_NUMBER_AREA_COMPACT_PX - 18
                        : overflowTopPx,
                      overlayTopOffsetPx + (isSingleRow ? 8 : 40)
                    ),
                  }}
                >
                  +{hiddenCount}{' '}
                  {!isSingleRow && (
                    <span className='hidden text-xs md:block'>more</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </JobDropZone>
      </div>
    );
  },
  // Enhanced memo comparison
  (prevProps, nextProps) => {
    if (
      prevProps.date.getTime() !== nextProps.date.getTime() ||
      prevProps.jobs.length !== nextProps.jobs.length ||
      prevProps.placeData?.id !== nextProps.placeData?.id ||
      !isSameMonth(prevProps.currentMonth, nextProps.currentMonth) ||
      prevProps.cellHeight !== nextProps.cellHeight ||
      prevProps.overlayTopOffsetPx !== nextProps.overlayTopOffsetPx ||
      prevProps.bookingsHidden !== nextProps.bookingsHidden ||
      prevProps.draftCellId !== nextProps.draftCellId ||
      prevProps.highlightedCells.size !== nextProps.highlightedCells.size ||
      prevProps.hiddenButtonCell !== nextProps.hiddenButtonCell ||
      prevProps.selectedDayViewDate !== nextProps.selectedDayViewDate
    ) {
      return false;
    }

    // Check highlighted cells content (symmetric equality)
    if (
      prevProps.highlightedCells.size > 0 ||
      nextProps.highlightedCells.size > 0
    ) {
      for (const cell of prevProps.highlightedCells) {
        if (!nextProps.highlightedCells.has(cell)) {
          return false;
        }
      }
      for (const cell of nextProps.highlightedCells) {
        if (!prevProps.highlightedCells.has(cell)) {
          return false;
        }
      }
    }

    // Deep check jobs array
    if (prevProps.jobs.length > 0) {
      const jobSig = (j: Job) => {
        const attrs = j.attributes ?? [];
        return [
          j.id,
          j.status,
          j.summary,
          j.start,
          j.end ?? '',
          j.scheduledStartTime ?? '',
          j.scheduledEndTime ?? '',
          j.assignee?.id ?? '',
          JSON.stringify(attrs),
          String(j.filtered ?? false),
        ].join('|');
      };
      for (let i = 0; i < prevProps.jobs.length; i++) {
        if (jobSig(prevProps.jobs[i]) !== jobSig(nextProps.jobs[i])) {
          return false;
        }
      }
    }

    return true;
  }
);

CalendarDateCell.displayName = 'CalendarDateCell';

export { CalendarDateCell };
